import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Sidenav from "../components/Sidenav";
import "../Console.css";

const styles = (
  <style>{`
    .articles-grid { display:grid; gap:16px; grid-template-columns:repeat(3,minmax(0,1fr)); }
    @media (max-width:1100px) { .articles-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
    @media (max-width:700px)  { .articles-grid { grid-template-columns:1fr; } }

    .article-card {
      background:#fff; border:1px solid #e5e7eb; border-radius:16px;
      overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,0.04),0 8px 24px rgba(0,0,0,0.05);
      transition:box-shadow 0.15s, transform 0.15s; display:flex; flex-direction:column;
      cursor:pointer;
    }
    .article-card:hover { box-shadow:0 4px 16px rgba(0,0,0,0.08),0 12px 32px rgba(0,0,0,0.07); transform:translateY(-1px); }

    .article-thumb {
      width:100%; height:160px; object-fit:cover; background:#f3f4f6;
      display:flex; align-items:center; justify-content:center; color:#d1d5db; font-size:2rem;
      position:relative;
    }
    .article-thumb img { width:100%; height:100%; object-fit:cover; }

    .article-menu-wrap { position:absolute; top:8px; right:8px; }
    .article-menu-btn {
      width:30px; height:30px; border-radius:50%; border:none;
      background:rgba(0,0,0,0.45); backdrop-filter:blur(4px);
      color:#fff; font-size:13px; cursor:pointer;
      display:flex; align-items:center; justify-content:center;
      transition:background 0.15s;
    }
    .article-menu-btn:hover { background:rgba(0,0,0,0.65); }
    .article-dropdown {
      position:absolute; top:36px; right:0;
      background:#fff; border:1px solid #e5e7eb; border-radius:10px;
      box-shadow:0 4px 20px rgba(0,0,0,0.12); overflow:hidden;
      min-width:130px; z-index:10;
    }
    .article-dropdown button {
      width:100%; border:none; background:none; text-align:left;
      padding:10px 14px; font-size:13px; font-weight:600; cursor:pointer;
      display:flex; align-items:center; gap:8px; color:#374151;
    }
    .article-dropdown button:hover { background:#f9fafb; }
    .article-dropdown .drop-delete { color:#b91c1c; }
    .article-dropdown .drop-delete:hover { background:#fff1f2; }

    .article-body { padding:16px; flex:1; display:flex; flex-direction:column; gap:8px; }
    .article-title { font-weight:800; font-size:15px; line-height:1.35; color:#111827; }
    .article-meta { display:flex; align-items:center; gap:10px; font-size:12px; color:#6b7280; flex-wrap:wrap; }

    .status-pill {
      display:inline-flex; align-items:center; padding:2px 10px; border-radius:9999px;
      font-size:11px; font-weight:700;
    }
    .status-pill.published { background:#dcfce7; color:#15803d; }
    .status-pill.draft     { background:#fef9c3; color:#a16207; }


    .empty-articles {
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      padding:80px 32px; text-align:center;
    }
    .empty-icon-wrap {
      width:90px; height:90px; border-radius:24px;
      background:#f3f4f6; border:1px solid #e5e7eb;
      display:flex; align-items:center; justify-content:center;
      margin-bottom:24px;
    }
    .empty-icon-wrap i { font-size:2.2rem; color:#9ca3af; }
    .empty-articles h3 { font-size:18px; font-weight:800; color:#111827; margin:0 0 8px; }
    .empty-articles p  { font-size:14px; color:#9ca3af; margin:0; max-width:280px; line-height:1.6; }
  `}</style>
);

function formatDate(dt) {
  return new Date(dt).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const Articles = ({ domain, userData }) => {
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenu]);

  const handleCardClick = (article) => {
    if (article.status === "published") {
      window.open(`/article/${article._id}`, "_blank");
    } else {
      navigate(`/articles/${article._id}/edit`);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/articles", { credentials: "same-origin" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setArticles(await res.json());
    } catch (e) {
      console.error(e);
      setErr("Failed to load articles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      const res = await fetch(`/api/articles/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setArticles((prev) => prev.filter((a) => a._id !== id));
    } catch (e) {
      console.error(e);
      alert("Failed to delete article.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Articles | BLEEP</title>
        <meta name="description" content="Manage BLEEP Articles." />
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />

        <div
          style={{
            flex: 1,
            height: "calc(100vh - 94px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,0,0,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              className="row d-flex align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div className="col" style={{ padding: 10 }}>
                <b style={{ fontSize: "28px", textShadow: "#1c1c1c 1px 2px 0px" }}>
                  <i className="fas fa-newspaper" /> Articles
                </b>
                <p style={{ paddingTop: 8, textShadow: "#1c1c1c 1px 1px 0px", marginBottom: 14 }}>
                  Manage BLEEP Articles
                </p>
                <button
                  className="btn btn-light btn-lg"
                  style={{
                    borderRadius: "30px",
                    boxShadow: "rgba(0,0,0,0.25) 0px 3px 8px",
                    paddingBlock: "10px",
                    paddingInline: "30px",
                  }}
                  onClick={() => navigate("/articles/new")}
                >
                  <span style={{ marginRight: 10 }}>
                    <i className="fa-solid fa-plus-circle" />
                  </span>
                  <b>Write Article</b>
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="p-4" style={{ backgroundColor: "#ebebeb", minHeight: "calc(100% - 160px)" }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: "60px", color: "#9ca3af" }}>
                <i className="fas fa-spinner fa-spin me-2" />Loading articles…
              </div>
            ) : err ? (
              <div style={{ textAlign: "center", padding: "60px", color: "#b91c1c" }}>{err}</div>
            ) : articles.length === 0 ? (
              <div className="empty-articles">
                <div className="empty-icon-wrap">
                  <i className="fas fa-newspaper" />
                </div>
                <h3>No articles yet</h3>
                <p>Articles you write will appear here. Use the button above to get started.</p>
              </div>
            ) : (
              <div className="articles-grid">
                {articles.map((article) => (
                  <div
                    key={article._id}
                    className="article-card"
                    onClick={() => handleCardClick(article)}
                  >
                    <div className="article-thumb">
                      {article.headerImageUrl ? (
                        <img src={article.headerImageUrl} alt={article.title} />
                      ) : (
                        <i className="fas fa-newspaper" />
                      )}
                      <div
                        className="article-menu-wrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="article-menu-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenu(openMenu === article._id ? null : article._id);
                          }}
                        >
                          <i className="fas fa-ellipsis-vertical" />
                        </button>
                        {openMenu === article._id && (
                          <div className="article-dropdown">
                            <button
                              onClick={() => {
                                setOpenMenu(null);
                                navigate(`/articles/${article._id}/edit`);
                              }}
                            >
                              <i className="fas fa-pen-to-square" /> Edit
                            </button>
                            <button
                              className="drop-delete"
                              onClick={() => {
                                setOpenMenu(null);
                                handleDelete(article._id);
                              }}
                            >
                              <i className="fas fa-trash-alt" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="article-body">
                      <div className="article-title">{article.title}</div>
                      <div className="article-meta">
                        <span>
                          <i className="fas fa-user me-1" />
                          {article.author || "—"}
                        </span>
                        <span>
                          <i className="fas fa-calendar me-1" />
                          {formatDate(article.dateTime)}
                        </span>
                        <span
                          className={`status-pill ${article.status === "published" ? "published" : "draft"}`}
                        >
                          {article.status === "published" ? "Published" : "Draft"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Articles;
